import React, { useCallback, useEffect } from 'react';
import EnterpriseSsoButtons from './molecules/EnterpriseSsoButtons';
import GitHubSsoButton from './GitHubSsoButton';
import HrWithOr from './HrWithOr';
import userFlowSlice from '../features/userFlow/userFlowSlice';
import { CustomInput, CustomPasswordInput, SubmitButton, validateEmail } from './common/formElements';
import { Field, FieldProps, Form, Formik, FormikErrors } from 'formik';
import { Icon, Spinner } from '@blueprintjs/core';
import { navigateWindow } from '../utils';
import { Link } from 'gatsby';
import { userActions } from '../state/user/userActions';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from '../state/rootReducer';
import config from '../config';
import { useGetEnterpriseConfigQuery } from '../apiHooks/enterpriseConfigApi';

interface ISignInProps {
    onNavigateAway?: () => void;
    onClickSignUp?: () => void;
}

const initialValues = { email: '', password: '' };

export default function SignIn({ onNavigateAway, onClickSignUp }: ISignInProps) {
    const dispatch = useDispatch();
    const gitHubSignIn = useCallback(
        (payload: { areTermsAcceptedForSignUp?: boolean }) => dispatch(userFlowSlice.actions.gitHubSignIn(payload)),
        [dispatch]
    ) as unknown as typeof userFlowSlice.actions.gitHubSignIn;
    const gitHubSignInLoadingState = useSelector((state: IRootState) => state.userFlow.gitHubSignInLoadingState);
    const isSignedIn = useSelector((state: IRootState) => state.user.isSignedIn);
    const isSignInPasswordFieldShown = useSelector((state: IRootState) => state.userFlow.isSignInPasswordFieldShown);
    const signInLoadingState = useSelector((state: IRootState) => state.user.signInLoadingState);
    const enterpriseSignInLoadingState = useSelector((state: IRootState) => state.userFlow.enterpriseSignInLoadingState);
    const { data: enterpriseConfig, isError: isEnterpriseConfigError } = useGetEnterpriseConfigQuery(undefined, { skip: !config.isEnterprise });
    const ssoConnections = enterpriseConfig?.sso_connections ?? [];
    const showEmailField = !config.isEnterprise ||
        new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get('show_email') === 'true' ||
        isEnterpriseConfigError ||
        (enterpriseConfig !== undefined && ssoConnections.length === 0);

    useEffect(() => {
        if (isSignedIn && !onNavigateAway) {
            const isPopup = new URLSearchParams(window.location.search).get('popup') === 'true';
            if (isPopup) {
                dispatch(userFlowSlice.actions.setSignInState({ signInState: { redirectTo: '/', isPopup: true } }));
                return;
            }
            navigateWindow('/');
        }
    }, []);

    useEffect(() => {
        return () => {
            dispatch(userActions.clearSignInErrors());
        };
    }, [dispatch]);

    const validate = (values: typeof initialValues): FormikErrors<typeof initialValues> => {
        const errors: FormikErrors<typeof initialValues> = {};
        const emailError = validateEmail(values.email);
        if (emailError) {
            errors.email = emailError;
        }
        return errors;
    };

    const onSubmit = ({ email, password }: typeof initialValues): void => {
        if (!isSignInPasswordFieldShown) {
            dispatch(userFlowSlice.actions.enterpriseSignIn({ email }));
        } else {
            // TODO: Re-attempt saml2 sign in if email has changed
            dispatch(userActions.signIn(email, password));
        }
    };

    if (gitHubSignInLoadingState.isLoading) {
        return (
            <div style={{ paddingTop: 2 }}>
                <h3 className='text-2xl font-medium text-gray-900 text-center pb-4'>Login to BioLib</h3>
                <Spinner/>
            </div>
        );
    }

    return (
        <Formik
            initialValues={initialValues}
            validate={validate}
            onSubmit={onSubmit}
            validateOnChange={true}
            validateOnBlur={false}
        >
            {({ values }) => (
                <Form method='POST' noValidate={true}>
                    <h3 className='text-2xl font-medium text-gray-900 text-center pb-4'>Login to BioLib</h3>
                    {config.isEnterprise ?
                        <>
                            <EnterpriseSsoButtons />
                            {showEmailField && <HrWithOr/>}
                        </> :
                        <>
                            <GitHubSsoButton githubSignIn={gitHubSignIn}/>
                            <HrWithOr/>
                        </>
                    }
                    {showEmailField && (
                        <>
                            <Field name='email' component={EmailInput}/>
                            {isSignInPasswordFieldShown ?
                                <>
                                    <b>
                                        Password
                                        <Link
                                            className='is-pulled-right'
                                            onClick={onNavigateAway}
                                            tabIndex={-1}
                                            to={`/reset-password/?email=${encodeURIComponent(values.email)}`}
                                        >
                                            Forgot password?
                                        </Link>
                                    </b>
                                    <Field name='password' component={PasswordInput}/>
                                </> : null
                            }
                            <p className='has-text-danger is-size-7' style={{ marginBottom: '10px' }}>
                                {signInLoadingState.errorMessage}
                            </p>
                            <SubmitButton
                                isSubmitting={signInLoadingState.isLoading || enterpriseSignInLoadingState.isLoading}
                                text='Login with Email'
                                fill={true}
                            />
                        </>
                    )}
                    {config.isEnterprise ? null : (
                        <>
                            <hr/>
                            <p className='has-text-centered'>
                                New to BioLib?{' '}
                                {onClickSignUp ?
                                    <a onClick={onClickSignUp}>
                                        Create an account <Icon icon='chevron-right'/>
                                    </a> : <Link to='/sign-up/'>Create an account<Icon icon='chevron-right'/></Link>
                                }
                            </p>
                        </>
                    )}
                </Form>
            )}
        </Formik>
    );
}

const EmailInput: React.FC<FieldProps> = CustomInput({
    autoFocus: true,
    label: 'Email',
    leftIcon: 'envelope',
    required: true,
    type: 'email',
});

const PasswordInput: React.FC<FieldProps> = CustomPasswordInput({ autoFocus: true });
